import { useEffect, useRef, useState } from 'react';
import { ApiResponse } from '../types';
import { profileRoutes, resolveCloud } from '../lib/models';

function ShareButton() {
  const [copied, setCopied] = useState(false);
  function share() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <button className="share-btn" onClick={share} title="Copy link to this view">
      {copied ? <><span aria-hidden="true">✓</span> Copied</> : <><span aria-hidden="true">⎘</span> Share</>}
    </button>
  );
}

function DownloadButton({ apiData }: { apiData: ApiResponse }) {
  function download() {
    const allRegions = [...new Set(apiData.models.map(m => m.region))].sort();

    const payload = {
      exportedAt: new Date().toISOString(),
      dataUpdatedAt: apiData.updatedAt,
      note: 'criProfiles lists profiles callable FROM the region; destinations are the regions requests may be routed to from that source.',
      regions: allRegions.map(region => {
        // modelId alone isn't a safe key across clouds — dedupe on cloud+modelId
        const seen = new Set<string>();
        const nativeModels = apiData.models
          .filter(m => m.region === region && !m.isCri)
          .filter(m => {
            const cloud = resolveCloud(m);
            const key = `${cloud}|${m.modelId}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .map(m => ({ modelId: m.modelId, cloud: resolveCloud(m) }))
          .sort((a, b) => a.modelId.localeCompare(b.modelId) || a.cloud.localeCompare(b.cloud));
        return {
          region,
          nativeModels,
          criProfiles: apiData.inferenceProfiles
            .filter(p => profileRoutes(p)[region])
            .map(p => ({
              profileId: p.profileId,
              destinations: profileRoutes(p)[region],
            }))
            .sort((a, b) => a.profileId.localeCompare(b.profileId)),
        };
      }),
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'model-availability.json';
    a.click();
    URL.revokeObjectURL(url);
  }
  return (
    <button className="download-btn" onClick={download} title="Download full availability and routing data as JSON">
      <span aria-hidden="true">⬇</span> Export JSON
    </button>
  );
}

interface Props {
  search: string;
  onSearch: (v: string) => void;
  provider: string;
  onProvider: (v: string) => void;
  providers: string[];
  activeOnly: boolean;
  onActiveOnly: (v: boolean) => void;
  showModalities?: boolean;
  inputModality: string;
  onInputModality: (v: string) => void;
  inputModalities: string[];
  outputModality: string;
  onOutputModality: (v: string) => void;
  outputModalities: string[];
  hasActiveFilters: boolean;
  onClear: () => void;
  resultCount: number;
  totalCount: number;
  apiData: ApiResponse | null;
}

export function FilterBar({
  search, onSearch,
  provider, onProvider, providers,
  activeOnly, onActiveOnly,
  showModalities = false,
  inputModality, onInputModality, inputModalities,
  outputModality, onOutputModality, outputModalities,
  hasActiveFilters, onClear,
  resultCount, totalCount,
  apiData,
}: Props) {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchFocused, setSearchFocused] = useState(false);

  // "/" jumps to search, same as GitHub — but only when the user isn't
  // already typing somewhere (including inside this box, so a literal "/"
  // in a search term still works normally).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== '/') return;
      const target = e.target as HTMLElement | null;
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA'
        || target?.tagName === 'SELECT' || target?.isContentEditable) return;
      e.preventDefault();
      searchInputRef.current?.focus();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className="filters">
      <div className="search-wrap">
        <input
          ref={searchInputRef}
          type="search"
          placeholder="Search model, ID, or provider…"
          title="Press / to search"
          aria-label="Search model, ID, or provider"
          value={search}
          onChange={e => onSearch(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
        />
        {!search && !searchFocused && <kbd className="search-hint" aria-hidden="true">/</kbd>}
      </div>

      <select value={provider} onChange={e => onProvider(e.target.value)} aria-label="Provider">
        <option value="">All providers</option>
        {providers.map(p => <option key={p} value={p}>{p}</option>)}
      </select>

      {showModalities && (
        <>
          <select value={inputModality} onChange={e => onInputModality(e.target.value)} aria-label="Input modality">
            <option value="">All inputs</option>
            {inputModalities.map(m => <option key={m} value={m}>{m.charAt(0) + m.slice(1).toLowerCase()}</option>)}
          </select>

          <select value={outputModality} onChange={e => onOutputModality(e.target.value)} aria-label="Output modality">
            <option value="">All outputs</option>
            {outputModalities.map(m => <option key={m} value={m}>{m.charAt(0) + m.slice(1).toLowerCase()}</option>)}
          </select>
        </>
      )}

      <label className="filter-toggle">
        <input type="checkbox" checked={activeOnly} onChange={e => onActiveOnly(e.target.checked)} />
        Active only
      </label>

      <span className="filter-count" aria-live="polite">
        {resultCount === totalCount
          ? `${totalCount} models`
          : `${resultCount} of ${totalCount} models`}
      </span>

      {hasActiveFilters && (
        <button className="clear-filters-btn" onClick={onClear}>
          Clear filters
        </button>
      )}

      <ShareButton />
      {apiData && <DownloadButton apiData={apiData} />}
    </div>
  );
}
